export type TBigWinMatch = {
  symbol: string;
  payout: number;
  reelIndexes: number[];
  reelCount: number;
  visibleCount: number;
};

type TBigWinConfig = {
  paytable?: Record<string, unknown>;
  symbolsReplacedByWild?: Record<string, boolean>;
  countToTriggerFreeSpins?: number;
  wild?: string;
  scat?: string;
  coin?: string;
  coinFeature?: { coin?: string; coinTrigger?: number };
};

export type TFreespinsMatch = {
  symbol: string;
  reelIndexes: number[];
  reelCount: number;
  visibleCount: number;
};

export type THoldAndSpinMatch = {
  symbol: string;
  reelIndexes: number[];
  visibleCount: number;
};

type TTimedSearch = {
  timeoutMs?: number;
  now?: () => number;
};

const getTimeoutChecker = (props: TTimedSearch) => {
  const now = props.now ?? Date.now;
  const deadline = now() + (props.timeoutMs ?? 5_000);
  return () => now() >= deadline;
};

const payoutFor = (value: unknown): number => {
  if (!Array.isArray(value)) { return 0; }
  return value.reduce<number>((highest, payout) => (
    typeof payout === 'number' && Number.isFinite(payout)
      ? Math.max(highest, payout)
      : highest
  ), 0);
};

const getPayingSymbols = (config: TBigWinConfig) => {
  const paytable = config.paytable ?? {};
  const explicitlyRegular = Object.entries(config.symbolsReplacedByWild ?? {})
    .filter(([, canBeReplaced]) => canBeReplaced)
    .map(([symbol]) => symbol);
  const specialSymbols = new Set([
    'wild',
    'scat',
    'coin',
    config.wild,
    config.scat,
    config.coin,
    config.coinFeature?.coin,
  ].filter((symbol): symbol is string => typeof symbol === 'string'));
  const symbols = explicitlyRegular.length > 0
    ? explicitlyRegular
    : Object.keys(paytable).filter(symbol => !specialSymbols.has(symbol));

  return symbols
    .map(symbol => ({ symbol, payout: payoutFor(paytable[symbol]) }))
    .filter(candidate => candidate.payout > 0)
    .sort((left, right) => right.payout - left.payout);
};

const visibleSymbolCount = (
  reel: string[],
  startIndex: number,
  rows: number,
  symbol: string,
) => {
  let count = 0;
  for (let row = 0; row < rows; row += 1) {
    if (reel[(startIndex + row) % reel.length] === symbol) { count += 1; }
  }
  return count;
};

/**
 * Finds the highest-paying regular symbol that can be shown on at least two
 * distinct reels. Every matching stop puts the symbol in row zero, keeping the
 * matches aligned while also preferring the stop with the most copies visible.
 */
export const findBigWinMatch = (props: {
  config: TBigWinConfig;
  reelStrips: string[][];
  rowsByReel: number[];
  fallbackReelIndexes?: number[];
  minimumReels?: number;
} & TTimedSearch): TBigWinMatch | null => {
  const minimumReels = props.minimumReels ?? 2;
  const timedOut = getTimeoutChecker(props);

  for (const candidate of getPayingSymbols(props.config)) {
    if (timedOut()) { return null; }
    let reelCount = 0;
    let visibleCount = 0;
    const reelIndexes: number[] = [];
    for (let reelIndex = 0; reelIndex < props.reelStrips.length; reelIndex += 1) {
      if (timedOut()) { return null; }
      const reel = props.reelStrips[reelIndex];
      const fallback = props.fallbackReelIndexes?.[reelIndex] ?? 0;
      if (!Array.isArray(reel) || reel.length === 0) {
        reelIndexes.push(fallback);
        continue;
      }

      let bestIndex = -1;
      let bestVisibleCount = 0;
      for (let index = 0; index < reel.length; index += 1) {
        if (timedOut()) { return null; }
        if (reel[index] !== candidate.symbol) { continue; }
        const count = visibleSymbolCount(
          reel,
          index,
          props.rowsByReel[reelIndex] ?? props.rowsByReel[0] ?? 0,
          candidate.symbol,
        );
        if (count > bestVisibleCount) {
          bestIndex = index;
          bestVisibleCount = count;
        }
      }

      if (bestIndex < 0) {
        reelIndexes.push(fallback);
        continue;
      }
      reelCount += 1;
      visibleCount += bestVisibleCount;
      reelIndexes.push(bestIndex);
    }

    if (reelCount >= minimumReels) {
      return { ...candidate, reelIndexes, reelCount, visibleCount };
    }
  }

  return null;
};

/**
 * Selects exactly countToTriggerFreeSpins distinct reels that can show a
 * scatter. Each selected stop maximizes the visible scatter count, while reel
 * stops outside the trigger remain unchanged.
 */
export const findFreespinsMatch = (props: {
  config: TBigWinConfig;
  reelStrips: string[][];
  rowsByReel: number[];
  fallbackReelIndexes?: number[];
} & TTimedSearch): TFreespinsMatch | null => {
  const symbol = props.config.scat;
  const requiredReels = props.config.countToTriggerFreeSpins;
  if (
    typeof symbol !== 'string'
    || typeof requiredReels !== 'number'
    || !Number.isInteger(requiredReels)
    || requiredReels < 1
  ) { return null; }
  const timedOut = getTimeoutChecker(props);

  let reelCount = 0;
  let visibleCount = 0;
  const reelIndexes: number[] = [];
  for (let reelIndex = 0; reelIndex < props.reelStrips.length; reelIndex += 1) {
    if (timedOut()) { return null; }
    const reel = props.reelStrips[reelIndex];
    const fallback = props.fallbackReelIndexes?.[reelIndex] ?? 0;
    if (reelCount >= requiredReels || !Array.isArray(reel) || reel.length === 0) {
      reelIndexes.push(fallback);
      continue;
    }

    let bestIndex = -1;
    let bestVisibleCount = 0;
    for (let index = 0; index < reel.length; index += 1) {
      if (timedOut()) { return null; }
      if (reel[index] !== symbol) { continue; }
      const count = visibleSymbolCount(
        reel,
        index,
        props.rowsByReel[reelIndex] ?? props.rowsByReel[0] ?? 0,
        symbol,
      );
      if (count > bestVisibleCount) {
        bestIndex = index;
        bestVisibleCount = count;
      }
    }

    if (bestIndex < 0) {
      reelIndexes.push(fallback);
      continue;
    }
    reelCount += 1;
    visibleCount += bestVisibleCount;
    reelIndexes.push(bestIndex);
  }

  return reelCount === requiredReels
    ? { symbol, reelIndexes, reelCount, visibleCount }
    : null;
};

/**
 * Finds one stop per reel whose combined visible coin count reaches the Hold &
 * Spin trigger. A small dynamic program chooses the lowest qualifying total;
 * each reel can contribute no more than its configured visible row count.
 */
export const findHoldAndSpinMatch = (props: {
  config: TBigWinConfig;
  reelStrips: string[][];
  rowsByReel: number[];
  fallbackReelIndexes?: number[];
} & TTimedSearch): THoldAndSpinMatch | null => {
  const trigger = props.config.coinFeature?.coinTrigger;
  const symbol = props.config.coinFeature?.coin ?? props.config.coin ?? 'coin';
  if (
    typeof trigger !== 'number'
    || !Number.isInteger(trigger)
    || trigger < 1
  ) { return null; }
  if (props.rowsByReel.reduce((total, rows) => total + Math.max(0, rows), 0) < trigger) {
    return null;
  }

  const timedOut = getTimeoutChecker(props);
  const optionsByReel: Array<Array<{ count: number; index: number }>> = [];

  for (let reelIndex = 0; reelIndex < props.reelStrips.length; reelIndex += 1) {
    if (timedOut()) { return null; }
    const reel = props.reelStrips[reelIndex];
    if (!Array.isArray(reel) || reel.length === 0) { return null; }
    const rows = props.rowsByReel[reelIndex] ?? props.rowsByReel[0] ?? 0;
    const fallback = props.fallbackReelIndexes?.[reelIndex] ?? 0;
    const normalizedFallback = ((fallback % reel.length) + reel.length) % reel.length;
    const indexByCount = new Map<number, number>();
    indexByCount.set(
      visibleSymbolCount(reel, normalizedFallback, rows, symbol),
      normalizedFallback,
    );

    for (let index = 0; index < reel.length; index += 1) {
      if (timedOut()) { return null; }
      const count = visibleSymbolCount(reel, index, rows, symbol);
      if (!indexByCount.has(count)) { indexByCount.set(count, index); }
    }
    optionsByReel.push(Array.from(indexByCount, ([count, index]) => ({ count, index })));
  }

  let combinations = new Map<number, number[]>([[0, []]]);
  for (const options of optionsByReel) {
    if (timedOut()) { return null; }
    const next = new Map<number, number[]>();
    for (const [total, indexes] of Array.from(combinations.entries())) {
      if (timedOut()) { return null; }
      for (const option of options) {
        if (timedOut()) { return null; }
        const nextTotal = total + option.count;
        if (!next.has(nextTotal)) { next.set(nextTotal, [...indexes, option.index]); }
      }
    }
    combinations = next;
  }

  const visibleCount = Array.from(combinations.keys())
    .filter(total => total >= trigger)
    .sort((left, right) => left - right)[0];
  if (visibleCount === undefined) { return null; }

  return {
    symbol,
    reelIndexes: combinations.get(visibleCount)!,
    visibleCount,
  };
};
